import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Card } from '../../components/ui/Card'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { apiFetch } from '../../lib/api-client'
import { t } from '../../lib/i18n'

interface Contract {
  id: string
  lease_id: string
  type: string
  monthly_rent_oere: number
  start_date: string
  end_date: string | null
  status: string
}

function statusVariant(s: string): 'green' | 'blue' | 'amber' | 'gray' | 'purple' | 'red' {
  switch (s) {
    case 'active': return 'green'
    case 'signed': return 'blue'
    case 'sent': return 'purple'
    case 'draft': return 'gray'
    case 'terminated': return 'red'
    default: return 'gray'
  }
}

function fmtRent(oere: number): string {
  return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(oere / 100)
}

export function ContractListPage() {
  const strings = t()
  const navigate = useNavigate()
  const [contracts, setContracts] = React.useState<Contract[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const generalError = strings.general.error

  React.useEffect(() => {
    apiFetch<Contract[]>('/api/v1/contracts')
      .then(setContracts)
      .catch(() => setError(generalError))
      .finally(() => setLoading(false))
  }, [generalError])

  const statusLabel = (s: string) => {
    const map: Record<string, string> = {
      draft: strings.contracts.statusDraft,
      sent: strings.contracts.statusSent,
      signed: strings.contracts.statusSigned,
      active: strings.contracts.statusActive,
      terminated: strings.contracts.statusTerminated,
    }
    return map[s] ?? s
  }

  return (
    <AppLayout
      actions={
        <Link to="/kontrakter/opret">
          <Button size="sm">
            <Plus size={13} style={{ marginRight: 4 }} />
            {strings.contracts.create}
          </Button>
        </Link>
      }
    >
      <Card padding="none">
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #e8ecf4', fontWeight: 600, fontSize: 13, color: '#0f172a' }}>
          {strings.contracts.title} {!loading && `(${contracts.length})`}
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>}
        {error && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>}

        {!loading && !error && contracts.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 12 }}>{strings.contracts.noResults}</p>
            <Link to="/kontrakter/opret"><Button size="sm"><Plus size={13} style={{ marginRight: 4 }} />{strings.contracts.create}</Button></Link>
          </div>
        )}

        {!loading && !error && contracts.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {[strings.contracts.type, strings.contracts.monthlyRent, strings.contracts.startDate, strings.contracts.status, ''].map((h) => (
                  <th key={h} style={{ padding: '8px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e8ecf4', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contracts.map((c, i) => (
                <ContractRow
                  key={c.id}
                  contract={c}
                  statusLabel={statusLabel(c.status)}
                  isLast={i === contracts.length - 1}
                  onClick={() => c.status === 'draft'
                    ? navigate(`/kontrakter/opret?draft=${c.id}`)
                    : navigate(`/lejeforhold/${c.id}`)
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </AppLayout>
  )
}

function ContractRow({ contract: c, statusLabel, isLast, onClick }: {
  contract: Contract
  statusLabel: string
  isLast: boolean
  onClick: () => void
}) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? '#f8fafc' : 'transparent', transition: 'background 0.1s', borderBottom: isLast ? 'none' : '1px solid #e8ecf4', cursor: 'pointer' }}
    >
      <td style={{ padding: '10px 20px', fontWeight: 500, color: '#0f172a', textTransform: 'uppercase' }}>{c.type}</td>
      <td style={{ padding: '10px 20px', color: '#0f172a', fontWeight: 500 }}>{fmtRent(c.monthly_rent_oere)}</td>
      <td style={{ padding: '10px 20px', color: '#64748b' }}>{new Date(c.start_date).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' })}</td>
      <td style={{ padding: '10px 20px' }}>
        <Chip variant={statusVariant(c.status)}>{statusLabel}</Chip>
      </td>
      <td style={{ padding: '10px 20px', textAlign: 'right', color: '#9ca3af', fontSize: 13 }}>→</td>
    </tr>
  )
}
